import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Video } from "lucide-react";
import { MEETING_TYPES } from "./meetingConstants";

export default function MeetingCreateDialog({ open, onOpenChange, prefill = {}, onCreated }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", meeting_type: "family_consultation", scheduled_at: "", duration_minutes: 45, notes: "" });

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, title: prefill.title || "", meeting_type: prefill.meeting_type || "family_consultation" }));
  }, [open]); // eslint-disable-line

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        duration_minutes: Number(form.duration_minutes) || 45,
        scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        related_lead_id: prefill.related_lead_id || null,
        related_opportunity_id: prefill.related_opportunity_id || null,
        related_chat_session_id: prefill.related_chat_session_id || null,
        related_task_id: prefill.related_task_id || null,
        service_lane: prefill.service_lane || null,
      };
      const { data } = await api.post("/meetings", payload);
      toast.success("Meeting created.");
      onOpenChange(false);
      if (onCreated) onCreated(data.meeting);
      else navigate(`/meetings/${data.meeting.id}`);
    } catch (e) { toast.error(errMsg(e, "Could not create meeting.")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="meeting-create-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-[#1C4358]"><Video size={18} /> New meeting</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[12px] text-[#4B5563]">Meeting title</Label>
            <Input data-testid="meeting-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Family Consultation with the Smiths" className="h-10" />
          </div>
          <div>
            <Label className="text-[12px] text-[#4B5563]">Meeting type</Label>
            <Select value={form.meeting_type} onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
              <SelectTrigger data-testid="meeting-type-select" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{MEETING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[12px] text-[#4B5563]">Date &amp; time</Label>
              <Input data-testid="meeting-datetime-input" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="h-10" />
            </div>
            <div>
              <Label className="text-[12px] text-[#4B5563]">Duration (min)</Label>
              <Input type="number" min={15} step={15} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="h-10" />
            </div>
          </div>
          <div>
            <Label className="text-[12px] text-[#4B5563]">Notes (internal)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Anything the host should know…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#D6E0E7] text-[#1C4358]">Cancel</Button>
          <Button data-testid="meeting-create-submit" onClick={create} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1">{saving ? <Loader2 size={14} className="animate-spin" /> : null} Create meeting</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
